import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Calendar as CalendarIcon, AlertCircle, UserPlus, Search, ChevronDown, ChevronUp, Check, FileDown, Printer, Utensils, MapPin, Pencil, ChevronLeft, ChevronRight, ArrowUpDown, Building2, FlaskConical } from 'lucide-react';
import { formatDateBR } from '../utils/dateUtils';

const APPT_PER_PAGE = 50;

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [units, setUnits] = useState([]);
  const [procedimentos, setProcedimentos] = useState({});
  const [systemConfig, setSystemConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPatientDialogOpen, setNewPatientDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [siglas, setSiglas] = useState({});
  const [subtipos, setSubtipos] = useState([]);
  const [subtipoSearch, setSubtipoSearch] = useState('');
  const [subtipoListOpen, setSubtipoListOpen] = useState(false);
  const [vagasListOpen, setVagasListOpen] = useState(false);
  const [confirmacaoDialogOpen, setConfirmacaoDialogOpen] = useState(false);
  const [ultimoAgendamentoId, setUltimoAgendamentoId] = useState(null);
  const [slotSemHorario, setSlotSemHorario] = useState(false);
  const [patientSearch, setPatientSearch] = useState(''); // Busca de paciente
  const [patientListOpen, setPatientListOpen] = useState(false); // Lista de pacientes expandida
  const [selectedPatient, setSelectedPatient] = useState(null); // Paciente selecionado
  
  // Edição de consulta
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editSlots, setEditSlots] = useState([]);
  const [editSlotId, setEditSlotId] = useState('');
  const [editSlotTurno, setEditSlotTurno] = useState('');
  const [editSlotSemHorario, setEditSlotSemHorario] = useState(false);
  const [horariosPermitidos, setHorariosPermitidos] = useState([]);
  
  // Filtros
  const [filterTipo, setFilterTipo] = useState('');
  const [filterUnit, setFilterUnit] = useState('');
  const [filterDataInicio, setFilterDataInicio] = useState('');
  const [filterDataFim, setFilterDataFim] = useState('');
  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [activeTab, setActiveTab] = useState('Marcada');
  const [tabCounts, setTabCounts] = useState({ Marcada: 0, Realizada: 0 });
  const [searchInput, setSearchInput] = useState(''); // Input do campo de busca
  const [searchQuery, setSearchQuery] = useState(''); // Busca efetiva (com debounce)
  const [apptPage, setApptPage] = useState(1);
  const [totalAppt, setTotalAppt] = useState(0);
  const [sortOrder, setSortOrder] = useState('desc');
  const [originTab, setOriginTab] = useState('hospital'); // hospital | lab_municipal

  // Siglas que permitem seleção múltipla de exames
  const isLabSigla = (sigla) => ['LAB', 'LAB-MUN', 'LACEM'].includes(sigla);

  const [locationDefaults, setLocationDefaults] = useState({
    hospital_nome: 'Hospital Eurídice Santana',
    hospital_endereco: 'R. Profa. Helena, 486-600 - Santa Rita de Cássia, BA, 47150-000',
    lab_municipal_nome: 'Laboratório Municipal',
    lab_municipal_endereco: 'Travessa Osvaldo Fidelis S/N'
  });

  const HOSPITAL_DEFAULT = {
    nome: locationDefaults.hospital_nome,
    endereco: locationDefaults.hospital_endereco
  };

  const LAB_MUNICIPAL_DEFAULT = {
    nome: locationDefaults.lab_municipal_nome,
    endereco: locationDefaults.lab_municipal_endereco
  };

  const [formData, setFormData] = useState({
    patient_id: '',
    unit_id: '',
    appointment_type: 'Consulta',
    procedure_category: '',
    procedure_code: '',
    procedure_name: '',
    procedure_codes: [],  // Para LAB - múltipla seleção
    procedure_names: [],  // Para LAB - múltipla seleção
    specialty: '',
    sigla: '',
    slot_id: '',
    doctor: '',
    appointment_date: '',
    appointment_time: '',
    local_atendimento: HOSPITAL_DEFAULT.nome,
    local_endereco: HOSPITAL_DEFAULT.endereco,
    notes: '',
    requires_fasting: false,
    registration_number: ''  // Nº REG - Número de registro
  });

  const [newPatientData, setNewPatientData] = useState({
    name: '',
    sus_card: '',
    cpf: '',
    birth_date: '',
    address: '',
    phone: '',
    unit_id: ''
  });

  const ROLE_LEVELS = { master: 5, admin_geral: 4, admin: 3, marcador_geral: 2, marcador: 1 };
  const roleLevel = ROLE_LEVELS[user?.role] || 0;
  const isAdmin = roleLevel >= ROLE_LEVELS.admin;
  const isSecretaria = user?.is_secretaria === true;
  const isAdminGeral = roleLevel >= ROLE_LEVELS.admin_geral || isSecretaria;
  const isMarcadorGeral = user?.role === 'marcador_geral';
  const hasFullAccess = roleLevel >= ROLE_LEVELS.marcador_geral || isSecretaria;
  // Admin e Marcador não podem trocar unidade (ficam na sua unidade), exceto admin da Secretaria
  const isAdminOnly = roleLevel === ROLE_LEVELS.admin && !isSecretaria;
  const canChangeUnit = isAdminOnly ? false : roleLevel >= ROLE_LEVELS.marcador_geral || isSecretaria;

  // Debounce da busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Buscar endereços configurados dos locais
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get('/settings/locations');
        setLocationDefaults({
          hospital_nome: resp.data.hospital_nome || 'Hospital Eurídice Santana',
          hospital_endereco: resp.data.hospital_endereco || '',
          lab_municipal_nome: resp.data.lab_municipal_nome || 'Laboratório Municipal',
          lab_municipal_endereco: resp.data.lab_municipal_endereco || 'Travessa Osvaldo Fidelis S/N'
        });
      } catch (e) {
        console.warn('Falha ao carregar endereços dos locais, usando padrão.');
      }
    })();
  }, []);

  // Reset para página 1 quando filtros mudam
  const prevFiltersRef = React.useRef('');
  useEffect(() => {
    const filtersKey = `${activeTab}-${filterTipo}-${filterUnit}-${searchQuery}-${filterDataInicio}-${filterDataFim}-${filterMonth}-${sortOrder}-${originTab}`;
    if (prevFiltersRef.current && prevFiltersRef.current !== filtersKey) {
      setApptPage(1);
      fetchData(1);
    } else {
      fetchData(apptPage);
    }
    prevFiltersRef.current = filtersKey;
  }, [apptPage, filterTipo, filterUnit, activeTab, searchQuery, filterDataInicio, filterDataFim, filterMonth, sortOrder, originTab]);

  const fetchData = async (pageToFetch) => {
    try {
      const pg = pageToFetch || apptPage;
      
      // Calcular datas efetivas: filtro manual tem prioridade, senão usar mês selecionado
      let efDataInicio = filterDataInicio;
      let efDataFim = filterDataFim;
      if (!efDataInicio && !efDataFim && filterMonth) {
        const [year, month] = filterMonth.split('-').map(Number);
        efDataInicio = `${year}-${String(month).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month, 0).getDate();
        efDataFim = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }
      
      // Construir query params para filtros
      let queryParams = new URLSearchParams();
      if (filterTipo) queryParams.append('tipo', filterTipo);
      if (activeTab) queryParams.append('status', activeTab);
      if (filterUnit) queryParams.append('unit_id', filterUnit);
      if (searchQuery.trim()) queryParams.append('search', searchQuery.trim());
      if (efDataInicio) queryParams.append('data_inicio', efDataInicio);
      if (efDataFim) queryParams.append('data_fim', efDataFim);
      queryParams.append('origin', originTab);
      queryParams.append('page', pg);
      queryParams.append('per_page', APPT_PER_PAGE);
      queryParams.append('sort_order', sortOrder);
      const queryString = queryParams.toString();
      
      // Construir params base para contadores (sem status, page, per_page)
      let countParams = new URLSearchParams();
      if (filterTipo) countParams.append('tipo', filterTipo);
      if (filterUnit) countParams.append('unit_id', filterUnit);
      if (searchQuery.trim()) countParams.append('search', searchQuery.trim());
      if (efDataInicio) countParams.append('data_inicio', efDataInicio);
      if (efDataFim) countParams.append('data_fim', efDataFim);
      countParams.append('origin', originTab);
      const countString = countParams.toString();
      
      // Buscar dados paginados + contadores + dados básicos em paralelo
      const [apptRes, countsRes, patientsRes, configRes, procRes, siglasRes] = await Promise.all([
        api.get(`/appointments/all?${queryString}`),
        api.get(`/appointments/counts${countString ? '?' + countString : ''}`),
        api.get('/patients'),
        api.get('/system-config'),
        api.get('/procedimentos'),
        api.get('/siglas')
      ]);
      
      setAppointments(apptRes.data.data);
      setTotalAppt(apptRes.data.total);
      setPatients(patientsRes.data);
      setSystemConfig(configRes.data);
      setProcedimentos(procRes.data);
      setSiglas(siglasRes.data);
      setTabCounts(countsRes.data);

      // Admins, Admin Geral, Master e Marcador Geral buscam todas as unidades
      if (hasFullAccess) {
        const unitsRes = await api.get('/health-units');
        setUnits(unitsRes.data);
      } else {
        // Marcador: buscar apenas sua unidade
        const myUnitRes = await api.get('/health-units/my-unit');
        if (myUnitRes.data) {
          setUnits([myUnitRes.data]);
          // Pré-selecionar a unidade do marcador no formulário
          setFormData(prev => ({ ...prev, unit_id: myUnitRes.data.id }));
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Buscar vagas disponíveis quando sigla mudar
  const fetchAvailableSlots = async (sigla) => {
    if (!sigla) {
      setAvailableSlots([]);
      return;
    }
    
    try {
      // Lab Municipal: buscar por origin (vagas compartilhadas entre LAB-MUN e LACEM)
      const query = originTab === 'lab_municipal' 
        ? `/slots/available?origin=lab_municipal`
        : `/slots/available?sigla=${sigla}`;
      const response = await api.get(query);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Erro ao buscar vagas:', error);
      setAvailableSlots([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação de sigla obrigatória
    if (!formData.sigla || !formData.sigla.trim()) {
      toast.error('Procedimento (Sigla) é obrigatório.');
      return;
    }
    
    // Validação de tipo de procedimento específico obrigatório
    if (isLabSigla(formData.sigla)) {
      if (!formData.procedure_codes || formData.procedure_codes.length === 0) {
        toast.error('Selecione pelo menos um exame.');
        return;
      }
    } else if (subtipos.length > 0 && (!formData.procedure_code || !formData.procedure_code.trim())) {
      toast.error('Tipo de Procedimento Específico é obrigatório.');
      return;
    }
    
    // Validação de vaga obrigatória
    if (!formData.slot_id) {
      toast.error('Selecione uma vaga disponível para realizar a marcação.');
      return;
    }
    
    // Validação de data retroativa no frontend
    if (formData.appointment_date) {
      const now = new Date();
      const apptDate = new Date(formData.appointment_date + 'T' + (formData.appointment_time || '23:59'));
      if (apptDate < now) {
        toast.error('Não é permitido marcar consultas ou exames para datas e horários retroativos.');
        return;
      }
    }
    
    try {
      const response = await api.post('/appointments', { ...formData, force_duplicate: false });
      const novoAgendamento = response.data;
      
      toast.success('Consulta marcada com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchData();
      
      // Mostrar modal de confirmação com opção de baixar cartão
      setUltimoAgendamentoId(novoAgendamento.id);
      setConfirmacaoDialogOpen(true);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      // Duplicidade detectada (409)
      if (status === 409 && typeof detail === 'object' && detail.message) {
        if (detail.can_force && isAdminGeral) {
          // Admin Geral+ pode forçar - mostrar confirmação
          if (window.confirm(`${detail.message}\n\nDeseja forçar a marcação mesmo assim?`)) {
            try {
              const resp = await api.post('/appointments', { ...formData, force_duplicate: true });
              toast.success('Consulta marcada com sucesso (duplicidade forçada)!');
              setDialogOpen(false);
              resetForm();
              fetchData();
              setUltimoAgendamentoId(resp.data.id);
              setConfirmacaoDialogOpen(true);
            } catch (err2) {
              const d2 = err2.response?.data?.detail;
              toast.error(typeof d2 === 'string' ? d2 : d2?.message || 'Erro ao forçar marcação');
            }
          }
        } else {
          // Usuário sem permissão para forçar
          toast.error(detail.message);
        }
      } else {
        const msg = typeof detail === 'string' ? detail : detail?.message || 'Erro ao marcar consulta';
        toast.error(msg);
      }
    }
  };

  const handleDownloadCartao = async (appointmentId) => {
    try {
      const response = await api.get(`/appointments/${appointmentId}/cartao-confirmacao`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comprovante_agendamento_${appointmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Comprovante de agendamento baixado!');
    } catch (error) {
      toast.error('Erro ao baixar cartão de confirmação');
      console.error(error);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await api.put(`/appointments/${appointmentId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success('Status atualizado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleCancel = async (appointmentId) => {
    if (!window.confirm('Deseja cancelar esta consulta/exame?')) return;
    
    try {
      await api.delete(`/appointments/${appointmentId}`);
      toast.success('Consulta cancelada com sucesso!');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error('Erro ao cancelar consulta');
      }
    }
  };

  const openEditDialog = async (appt) => {
    setEditingAppt(appt);
    setEditDate('');
    setEditTime('');
    setEditSlotId('');
    setEditSlots([]);
    setEditDialogOpen(true);
    // Buscar vagas disponíveis da mesma sigla
    try {
      const res = await api.get(`/slots?sigla=${appt.sigla}`);
      const available = (res.data || []).filter(s => 
        s.quantidade_disponivel > 0 && 
        !s.deadline_blocked && 
        !s.bloqueado &&
        s.id !== appt.slot_id
      );
      setEditSlots(available);
    } catch {
      toast.error('Erro ao buscar vagas disponíveis');
    }
  };

  const handleSelectEditSlot = (slot) => {
    setEditSlotId(slot.id);
    setEditDate(slot.data);
    const temHorario = slot.horario && slot.horario.trim() !== '';
    setEditTime(temHorario ? slot.horario : '');
    setEditSlotTurno(slot.turno || '');
    setEditSlotSemHorario(!temHorario);
  };

  const handleEditAppointment = async () => {
    if (!editSlotId) {
      toast.error('Selecione uma vaga');
      return;
    }
    if (!editDate) {
      toast.error('Selecione uma vaga com data');
      return;
    }
    if (!editTime) {
      toast.error('Informe o horário do atendimento');
      return;
    }
    try {
      setEditLoading(true);
      await api.put(`/appointments/${editingAppt.id}/edit`, {
        appointment_date: editDate,
        appointment_time: editTime,
        new_slot_id: editSlotId
      });
      toast.success('Agendamento atualizado com sucesso!');
      setEditDialogOpen(false);
      setEditingAppt(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao editar agendamento');
    } finally {
      setEditLoading(false);
    }
  };

  const resetForm = () => {
    // Admin e Marcador: fixar na sua unidade
    const defaultUnitId = !canChangeUnit && user?.unit_id ? user.unit_id 
      : (!hasFullAccess && units.length > 0 ? units[0].id : '');
    setFormData({
      patient_id: '',
      unit_id: defaultUnitId,
      appointment_type: originTab === 'lab_municipal' ? 'Exame' : 'Consulta',
      procedure_category: '',
      procedure_code: '',
      procedure_name: '',
      procedure_codes: [],
      procedure_names: [],
      specialty: '',
      sigla: '',
      slot_id: '',
      doctor: '',
      appointment_date: '',
      appointment_time: '',
      local_atendimento: originTab === 'lab_municipal' ? LAB_MUNICIPAL_DEFAULT.nome : HOSPITAL_DEFAULT.nome,
      local_endereco: originTab === 'lab_municipal' ? LAB_MUNICIPAL_DEFAULT.endereco : HOSPITAL_DEFAULT.endereco,
      notes: '',
      requires_fasting: false,
      registration_number: ''
    });
    setSearchTerm('');
    setAvailableSlots([]);
    setSubtipos([]);
    setSubtipoSearch('');
    setSubtipoListOpen(false);
    setVagasListOpen(false);
    setSlotSemHorario(false);
    setHorariosPermitidos([]);
    setPatientSearch('');
    setPatientListOpen(false);
    setSelectedPatient(null);
  };

  // Função para filtrar pacientes por nome ou cartão SUS
  const getFilteredPatients = () => {
    if (!patientSearch.trim()) return [];
    
    const searchLower = patientSearch.toLowerCase().trim();
    const digitsOnly = searchLower.replace(/[\s.\-\/]/g, '');
    
    return patients.filter(patient => {
      if (patient.name.toLowerCase().includes(searchLower)) return true;
      // Busca flexível: comparar apenas dígitos para SUS e CPF
      if (digitsOnly.length >= 3) {
        const susDigits = (patient.sus_card || '').replace(/[\s.\-\/]/g, '');
        const cpfDigits = (patient.cpf || '').replace(/[\s.\-\/]/g, '');
        if (susDigits.includes(digitsOnly) || cpfDigits.includes(digitsOnly)) return true;
      }
      return false;
    }).slice(0, 10);
  };

  // Função para selecionar um paciente
  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    
    // Preencher automaticamente a unidade do paciente
    const patientUnit = units.find(u => u.id === patient.unit_id);
    
    setFormData({ 
      ...formData, 
      patient_id: patient.id,
      unit_id: patient.unit_id || formData.unit_id // Usar unidade do paciente se existir
    });
    
    setPatientSearch('');
    setPatientListOpen(false);
  };

  // Retorna true se a sigla pode ser usada:
  // - Se TODOS os procedimentos da sigla sao somente_admin_geral e o usuario NAO e admin geral -> bloqueia
  // - Caso contrario (pelo menos um procedimento livre), permite selecionar a sigla
  //   (o bloqueio por procedimento especifico acontece no momento da selecao do item)
  const canUseSigla = (sigla) => {
    const grupo = procedimentos.find?.(p => p.sigla === sigla);
    if (!grupo || !grupo.procedimentos || grupo.procedimentos.length === 0) return true;
    const todosRestritos = grupo.procedimentos.every(p => p.ativo !== false && p.somente_admin_geral === true);
    if (todosRestritos) return isAdminGeral;
    return true;
  };

  // Retorna true se o usuario pode marcar o PROCEDIMENTO especifico (item dentro da sigla)
  const canUseProcedimento = (sigla, procNomeOuCodigo) => {
    const grupo = procedimentos.find?.(p => p.sigla === sigla);
    if (!grupo || !grupo.procedimentos) return true;
    const item = grupo.procedimentos.find(p =>
      p.nome === procNomeOuCodigo || p.codigo === procNomeOuCodigo || p.id === procNomeOuCodigo
    );
    if (item?.somente_admin_geral) return isAdminGeral;
    return true;
  };

  // Função para selecionar uma sigla e buscar vagas e subtipos
  const handleSelectSigla = async (sigla) => {
    if (!canUseSigla(sigla)) {
      toast.error('Este procedimento só pode ser marcado por Administradores Gerais ou superior.');
      return;
    }
    const siglaInfo = siglas[sigla];
    setFormData({
      ...formData,
      sigla: sigla,
      slot_id: '',
      appointment_date: '',
      appointment_time: '',
      doctor: '',
      procedure_name: '',
      procedure_code: '',
      procedure_codes: [],
      procedure_names: []
    });
    setSubtipoSearch('');
    setSubtipoListOpen(true); // Abrir lista automaticamente ao selecionar sigla
    setVagasListOpen(true); // Abrir lista de vagas também
    setSlotSemHorario(false); // Reset estado de slot sem horário
    setHorariosPermitidos([]); // Reset horários permitidos
    
    // Buscar vagas disponíveis
    await fetchAvailableSlots(sigla);
    
    // Buscar subtipos de procedimentos
    try {
      const response = await api.get(`/subtipos/${sigla}`);
      setSubtipos(response.data);
    } catch (error) {
      console.error('Erro ao buscar subtipos:', error);
      setSubtipos([]);
    }
  };

  // Função para selecionar um subtipo de procedimento
  const handleSelectSubtipo = (subtipo) => {
    // Para LAB / LAB-MUN / LACEM, permitir seleção múltipla
    if (isLabSigla(formData.sigla)) {
      // Identidade do exame = NOME (codes podem estar vazios para exames sem SIGTAP)
      const idx = (formData.procedure_names || []).indexOf(subtipo.nome);
      if (idx >= 0) {
        // Remover EXATAMENTE este (preserva outros mesmo com codes vazios)
        const newNames = [...formData.procedure_names];
        const newCodes = [...formData.procedure_codes];
        newNames.splice(idx, 1);
        if (idx < newCodes.length) newCodes.splice(idx, 1);
        setFormData({
          ...formData,
          procedure_names: newNames,
          procedure_codes: newCodes
        });
      } else {
        // Adicionar
        setFormData({
          ...formData,
          procedure_codes: [...formData.procedure_codes, subtipo.codigo || ''],
          procedure_names: [...formData.procedure_names, subtipo.nome]
        });
      }
    } else {
      setFormData({
        ...formData,
        procedure_code: subtipo.codigo || '',
        procedure_name: subtipo.nome
      });
      setSubtipoSearch('');
      setSubtipoListOpen(false);
    }
  };

  // Verificar se um subtipo está selecionado (para LAB / LAB-MUN / LACEM)
  const isSubtipoSelected = (subtipo) => {
    if (isLabSigla(formData.sigla)) {
      // Identidade por nome (codes podem ser vazios e duplicados)
      return (formData.procedure_names || []).includes(subtipo.nome);
    }
    if (subtipo.nome && formData.procedure_name) {
      return formData.procedure_name === subtipo.nome;
    }
    if (subtipo.codigo && formData.procedure_code) {
      return formData.procedure_code === subtipo.codigo;
    }
    return false;
  };

  // Função para toggle da lista de subtipos
  const toggleSubtipoList = () => {
    setSubtipoListOpen(!subtipoListOpen);
  };

  // Filtrar subtipos por busca
  const getFilteredSubtipos = () => {
    if (!subtipoSearch) return subtipos;
    return subtipos.filter(s => 
      s.nome.toLowerCase().includes(subtipoSearch.toLowerCase()) ||
      s.codigo.includes(subtipoSearch)
    );
  };

  // Função para selecionar uma vaga
  const handleSelectSlot = async (slot) => {
    // Verificar se o slot tem horário definido
    const temHorario = slot.horario && slot.horario.trim() !== '';
    setSlotSemHorario(!temHorario);
    
    setFormData({
      ...formData,
      slot_id: slot.id,
      appointment_date: slot.data,
      appointment_time: temHorario ? slot.horario : '',
      doctor: slot.profissional,
      _slot_turno: slot.turno || ''
    });
    
    // Buscar horários permitidos para a sigla/mês do calendário
    if (!temHorario && slot.data && formData.sigla) {
      try {
        const dataObj = new Date(slot.data + 'T12:00:00');
        const mes = dataObj.getMonth() + 1;
        const ano = dataObj.getFullYear();
        const res = await api.get(`/api/slots/horarios-permitidos/${formData.sigla}/${mes}/${ano}`);
        const horarios = res.data?.horarios || [];
        // Filtrar por turno
        const turno = slot.turno || '';
        let horariosFiltrados = horarios;
        if (turno === 'M') {
          horariosFiltrados = horarios.filter(h => h < '12:00');
        } else if (turno === 'T') {
          horariosFiltrados = horarios.filter(h => h >= '12:00');
        }
        setHorariosPermitidos(horariosFiltrados);
      } catch {
        setHorariosPermitidos([]);
      }
    } else {
      setHorariosPermitidos([]);
    }
  };

  // Função para selecionar um procedimento
  const handleSelectProcedure = (proc) => {
    setFormData({
      ...formData,
      procedure_code: proc.codigo,
      procedure_name: proc.nome,
      procedure_category: formData.procedure_category,
      specialty: ''
    });
    setSearchTerm('');
  };

  // Função para obter especialidades do procedimento de consulta
  const getEspecialidades = () => {
    if (formData.procedure_category === 'CONSULTAS' && procedimentos.CONSULTAS) {
      return procedimentos.CONSULTAS[0]?.especialidades || [];
    }
    return [];
  };

  // Filtrar procedimentos por busca
  const getFilteredProcedures = () => {
    if (!formData.procedure_category || !procedimentos[formData.procedure_category]) {
      return [];
    }
    
    const procs = procedimentos[formData.procedure_category];
    if (!searchTerm) return procs;
    
    return procs.filter(p => 
      p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.codigo.includes(searchTerm)
    );
  };

  const resetNewPatientForm = () => {
    // Admin e Marcador: pré-preencher com a unidade do usuário
    const defaultUnitId = (user?.role === 'marcador' || user?.role === 'admin') ? user?.unit_id : '';
    setNewPatientData({
      name: '',
      sus_card: '',
      cpf: '',
      birth_date: '',
      address: '',
      phone: '',
      unit_id: defaultUnitId
    });
  };

  const handleNewPatientSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.post('/patients', newPatientData);
      const newPatient = response.data;
      
      toast.success('Paciente cadastrado com sucesso!');
      
      // Fechar modal de novo paciente primeiro
      setNewPatientDialogOpen(false);
      resetNewPatientForm();
      
      // Atualizar lista de pacientes e selecionar o novo paciente
      // Usar callback para garantir que o estado seja atualizado corretamente
      setPatients(prev => {
        const updatedPatients = [...prev, newPatient];
        // Usar setTimeout para garantir que o Select tenha os novos dados antes de selecionar
        setTimeout(() => {
          setFormData(prevForm => ({ ...prevForm, patient_id: newPatient.id }));
        }, 100);
        return updatedPatients;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar paciente');
    }
  };

  const openNewPatientDialog = () => {
    resetNewPatientForm();
    setNewPatientDialogOpen(true);
  };

  // Máscara para CPF
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .slice(0, 14);
  };

  // Máscara para Cartão SUS
  const formatSUSCard = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1 $2')
      .replace(/(\d{4})(\d)/, '$1 $2')
      .replace(/(\d{4})(\d)/, '$1 $2')
      .slice(0, 18);
  };

  // Máscara para Telefone
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .slice(0, 14);
    }
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  };

  const isDeadlinePassed = () => {
    if (!systemConfig?.deadline_date) return false;
    return new Date() > new Date(systemConfig.deadline_date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando consultas...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="appointments-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 tracking-tight">Consultas e Exames</h1>
          <p className="text-slate-600">Gerencie as marcações de consultas e exames</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-lg h-10 px-6 font-medium shadow-sm"
              onClick={() => resetForm()}
              disabled={isDeadlinePassed()}
              data-testid="add-appointment-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Marcação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Nova Marcação</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Paciente + Novo Paciente */}
              <div>
                <Label htmlFor="patient">Paciente *</Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    {/* Campo de busca ou paciente selecionado */}
                    {selectedPatient ? (
                      <div 
                        className="h-11 px-4 rounded-lg border border-teal-300 bg-teal-50 flex items-center justify-between cursor-pointer hover:bg-teal-100 transition-colors overflow-hidden"
                        onClick={() => {
                          setSelectedPatient(null);
                          setFormData({ ...formData, patient_id: '' });
                          setPatientListOpen(true);
                        }}
                        data-testid="selected-patient"
                      >
                        <div className="flex flex-col min-w-0 flex-1 mr-2">
                          <span className="font-medium text-teal-800 truncate">{selectedPatient.name}</span>
                          <span className="text-xs text-teal-600 truncate">SUS: {selectedPatient.sus_card}</span>
                        </div>
                        <span className="text-xs text-teal-500 hover:text-teal-700 whitespace-nowrap flex-shrink-0">✕ Trocar</span>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            type="text"
                            placeholder="Digite nome ou cartão SUS..."
                            value={patientSearch}
                            onChange={(e) => {
                              setPatientSearch(e.target.value);
                              setPatientListOpen(e.target.value.length > 0);
                            }}
                            onFocus={() => patientSearch.length > 0 && setPatientListOpen(true)}
                            className="h-11 pl-10 rounded-lg"
                            data-testid="patient-search-input"
                          />
                        </div>
                        
                        {/* Lista de pacientes encontrados */}
                        {patientListOpen && patientSearch.length > 0 && (
                          <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {getFilteredPatients().length > 0 ? (
                              getFilteredPatients().map((patient) => (
                                <div
                                  key={patient.id}
                                  onClick={() => handleSelectPatient(patient)}
                                  className="px-4 py-3 cursor-pointer hover:bg-teal-50 border-b border-slate-100 last:border-0 transition-colors"
                                  data-testid={`patient-option-${patient.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span className="font-medium text-slate-900">{patient.name}</span>
                                      <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                                        <span>SUS: <span className="font-mono text-teal-600">{patient.sus_card}</span></span>
                                        {patient.cpf && <span>CPF: {patient.cpf}</span>}
                                      </div>
                                    </div>
                                    <Check className="h-4 w-4 text-teal-600 opacity-0 group-hover:opacity-100" />
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-4 py-6 text-center text-slate-500">
                                <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm">Nenhum paciente encontrado</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Tente outro nome ou número do cartão SUS
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openNewPatientDialog}
                    className="h-11 px-3 border-teal-200 text-teal-700 hover:bg-teal-50 hover:border-teal-300"
                    title="Cadastrar novo paciente"
                    data-testid="new-patient-button"
                  >
                    <UserPlus className="h-5 w-5" />
                  </Button>
                </div>
                {patients.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Nenhum paciente cadastrado. Clique no botão ao lado para cadastrar.
                  </p>
                )}
                {!selectedPatient && patients.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Digite pelo menos 2 caracteres para buscar
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-6">
                  <Label htmlFor="unit">Unidade de Saúde *</Label>
                  <Select 
                    value={formData.unit_id} 
                    onValueChange={(value) => setFormData({ ...formData, unit_id: value })}
                    disabled={!canChangeUnit}
                  >
                    <SelectTrigger className="h-11 rounded-lg">
                      <SelectValue placeholder="Selecione a unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map(unit => (
                        <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!canChangeUnit && (
                    <p className="text-xs text-slate-500 mt-1">
                      Você só pode marcar consultas para pacientes da sua unidade.
                    </p>
                  )}
                </div>

                <div className="md:col-span-4">
                  <Label>Tipo *</Label>
                  <Select value={formData.appointment_type} onValueChange={(value) => {
                    setFormData({ 
                      ...formData, 
                      appointment_type: value,
                      sigla: '',
                      slot_id: '',
                      procedure_category: '',
                      procedure_code: '',
                      procedure_name: '',
                      specialty: '',
                      appointment_date: '',
                      appointment_time: '',
                      doctor: '',
                      local_atendimento: originTab === 'lab_municipal' ? LAB_MUNICIPAL_DEFAULT.nome : HOSPITAL_DEFAULT.nome,
                      local_endereco: originTab === 'lab_municipal' ? LAB_MUNICIPAL_DEFAULT.endereco : HOSPITAL_DEFAULT.endereco
                    });
                    setAvailableSlots([]);
                    setSubtipos([]);
                    setSubtipoSearch('');
                  }}>
                    <SelectTrigger className="h-11 rounded-lg" data-testid="appointment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Consulta">Consulta</SelectItem>
                      <SelectItem value="Exame">Exame</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-8">
                  <Label>Procedimento (Sigla) *</Label>
                  <Select 
                    value={formData.sigla} 
                    onValueChange={handleSelectSigla}
                  >
                    <SelectTrigger className="h-11 rounded-lg" data-testid="sigla-select">
                      <SelectValue placeholder="Selecione o procedimento" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(siglas)
                        .filter(([sigla, info]) => {
                          const siglaOrigin = info.origin || 'hospital';
                          if (siglaOrigin !== originTab) return false;
                          // Lab Municipal unificado: esconder LACEM (vai junto com LAB-MUN)
                          if (originTab === 'lab_municipal' && sigla === 'LACEM') return false;
                          return formData.appointment_type === 'Consulta' 
                            ? info.tipo === 'Consulta' 
                            : info.tipo === 'Exame';
                        })
                        .map(([sigla, info]) => (
                          <SelectItem 
                            key={sigla} 
                            value={sigla}
                            disabled={!canUseSigla(sigla)}
                          >
                            {sigla === 'LAB-MUN' ? 'LAB. MUNICIPAL - Exames + Coletas LACEM' : `${sigla} - ${info.nome}`}{!canUseSigla(sigla) ? ' (Restrito)' : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Seleção de Subtipo de Procedimento */}
                {formData.sigla && subtipos.length > 0 && (
                  <div className="md:col-span-12">
                    <Label>
                      {isLabSigla(formData.sigla) 
                        ? 'Procedimentos Laboratoriais (selecione um ou mais) *' 
                        : 'Tipo de Procedimento Específico *'}
                    </Label>
                    <div className="mt-2">
                      {/* Botão para expandir/ocultar lista */}
                      <button
                        type="button"
                        onClick={toggleSubtipoList}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                          (isLabSigla(formData.sigla) ? formData.procedure_codes.length > 0 : formData.procedure_code)
                            ? 'bg-teal-50 border-teal-300 text-teal-800' 
                            : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                        data-testid="subtipo-toggle"
                      >
                        <div className="flex items-center gap-2">
                          {isLabSigla(formData.sigla) && formData.procedure_codes.length > 0 ? (
                            <>
                              <Check className="h-4 w-4 text-teal-600" />
                              <div className="text-left">
                                <span className="text-sm font-medium">
                                  {formData.procedure_codes.length} exame(s) selecionado(s)
                                </span>
                                <span className="block text-xs text-teal-600 truncate max-w-md">
                                  {formData.procedure_names.slice(0, 3).join(', ')}
                                  {formData.procedure_names.length > 3 && '...'}
                                </span>
                              </div>
                            </>
                          ) : formData.procedure_code ? (
                            <>
                              <Check className="h-4 w-4 text-teal-600" />
                              <div className="text-left">
                                <span className="font-mono text-xs text-teal-600 block">{formData.procedure_code}</span>
                                <span className="text-sm font-medium">{formData.procedure_name}</span>
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-500">Clique para selecionar o procedimento específico</span>
                          )}
                        </div>
                        {subtipoListOpen ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </button>

                      {/* Lista expansível de subtipos */}
                      {subtipoListOpen && (
                        <div className="mt-2 border border-slate-200 rounded-lg bg-white shadow-lg overflow-hidden">
                          {/* Campo de busca */}
                          <div className="p-3 border-b border-slate-100 bg-slate-50">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                type="text"
                                placeholder="Buscar por nome ou código..."
                                value={subtipoSearch}
                                onChange={(e) => setSubtipoSearch(e.target.value)}
                                className="pl-10 h-10 rounded-lg bg-white"
                                data-testid="subtipo-search"
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-2">
                              {getFilteredSubtipos().length} procedimento(s) encontrado(s)
                              {isLabSigla(formData.sigla) && formData.procedure_codes.length > 0 && (
                                <span className="text-teal-600 font-medium ml-2">
                                  • {formData.procedure_codes.length} selecionado(s)
                                </span>
                              )}
                            </p>
                            {isLabSigla(formData.sigla) && (
                              <p className="text-xs text-amber-600 mt-1">
                                Clique nos exames para selecionar múltiplos procedimentos
                              </p>
                            )}
                          </div>
                          
                          {/* Lista de procedimentos */}
                          <div className="max-h-64 overflow-y-auto">
                            {getFilteredSubtipos().map((subtipo, index) => (
                              <div
                                key={index}
                                onClick={() => handleSelectSubtipo(subtipo)}
                                className={`px-4 py-3 cursor-pointer border-b border-slate-100 last:border-0 transition-colors ${
                                  isSubtipoSelected(subtipo)
                                    ? 'bg-teal-100 border-l-4 border-l-teal-600' 
                                    : 'hover:bg-slate-50'
                                }`}
                                data-testid={`subtipo-item-${subtipo.codigo}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {/* Checkbox visual para LAB */}
                                    {isLabSigla(formData.sigla) && (
                                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                        isSubtipoSelected(subtipo)
                                          ? 'bg-teal-600 border-teal-600' 
                                          : 'bg-white border-slate-300'
                                      }`}>
                                        {isSubtipoSelected(subtipo) && <Check className="h-3 w-3 text-white" />}
                                      </div>
                                    )}
                                    <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                                      {subtipo.codigo}
                                    </span>
                                  </div>
                                  {formData.sigla !== 'LAB' && isSubtipoSelected(subtipo) && (
                                    <Check className="h-4 w-4 text-teal-600" />
                                  )}
                                </div>
                                <p className={`text-sm mt-1 ${isLabSigla(formData.sigla) ? 'ml-8' : ''} ${
                                  isSubtipoSelected(subtipo) ? 'text-teal-800 font-medium' : 'text-slate-700'
                                }`}>
                                  {subtipo.nome}
                                  {subtipo.is_lacem && (
                                    <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold bg-orange-100 text-orange-700 rounded" data-testid={`lacem-badge-${subtipo.codigo}`}>LACEM</span>
                                  )}
                                </p>
                              </div>
                            ))}
                            {getFilteredSubtipos().length === 0 && (
                              <div className="p-6 text-center text-slate-500">
                                <Search className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                                <p className="text-sm">Nenhum procedimento encontrado</p>
                                <p className="text-xs text-slate-400 mt-1">Tente buscar por outro termo</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Botão de confirmar seleção para LAB */}
                          {isLabSigla(formData.sigla) && formData.procedure_codes.length > 0 && (
                            <div className="p-3 border-t border-slate-200 bg-slate-50">
                              <Button
                                type="button"
                                onClick={() => setSubtipoListOpen(false)}
                                className="w-full bg-teal-700 hover:bg-teal-800"
                                data-testid="confirm-lab-selection"
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Confirmar {formData.procedure_codes.length} exame(s)
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Seleção de Vaga Disponível */}
                {formData.sigla && (
                  <div className="md:col-span-12">
                    <Label>Vaga Disponível *</Label>
                    <div className="mt-2">
                      {/* Botão para expandir/ocultar lista de vagas */}
                      <button
                        type="button"
                        onClick={() => setVagasListOpen(!vagasListOpen)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                          formData.slot_id 
                            ? 'bg-teal-50 border-teal-300 text-teal-800' 
                            : availableSlots.length === 0
                              ? 'bg-amber-50 border-amber-300 text-amber-800'
                              : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                        }`}
                        data-testid="vagas-toggle"
                      >
                        <div className="flex items-center gap-2">
                          {formData.slot_id ? (
                            <>
                              <Check className="h-4 w-4 text-teal-600" />
                              <div className="text-left">
                                <span className="text-sm font-medium">
                                  {formData.appointment_date && new Date(formData.appointment_date + 'T12:00:00').toLocaleDateString('pt-BR', { 
                                    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
                                  })} às {formData.appointment_time}
                                </span>
                                <span className="block text-xs text-teal-600">Prof: {formData.doctor}</span>
                              </div>
                            </>
                          ) : availableSlots.length === 0 ? (
                            <span className="text-amber-700">Nenhuma vaga disponível para {formData.sigla}</span>
                          ) : (
                            <span className="text-slate-500">
                              Clique para selecionar uma vaga ({availableSlots.length} disponíveis)
                            </span>
                          )}
                        </div>
                        {vagasListOpen ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </button>

                      {/* Lista expansível de vagas */}
                      {vagasListOpen && availableSlots.length > 0 && (
                        <div className="mt-2 border border-slate-200 rounded-lg bg-white shadow-lg overflow-hidden">
                          <div className="p-3 border-b border-slate-100 bg-slate-50">
                            <p className="text-xs text-slate-600">
                              {availableSlots.length} vaga(s) disponível(is) para <strong>{formData.sigla}</strong>
                            </p>
                          </div>
                          <div className="max-h-64 overflow-y-auto">
                            {availableSlots.map((slot) => (
                              <div
                                key={slot.id}
                                onClick={() => {
                                  handleSelectSlot(slot);
                                  setVagasListOpen(false);
                                }}
                                className={`px-4 py-3 cursor-pointer border-b border-slate-100 last:border-0 transition-colors ${
                                  formData.slot_id === slot.id 
                                    ? 'bg-teal-100 border-l-4 border-l-teal-600' 
                                    : 'hover:bg-slate-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-semibold text-slate-900">
                                      {new Date(slot.data + 'T12:00:00').toLocaleDateString('pt-BR', { 
                                        weekday: 'long', 
                                        day: '2-digit', 
                                        month: '2-digit',
                                        year: 'numeric'
                                      })}
                                    </span>
                                    {slot.horario ? (
                                      <span className="text-teal-600 font-mono ml-2">{slot.horario}</span>
                                    ) : (
                                      <span className="text-amber-600 ml-2">
                                        {slot.turno === 'M' ? 'Manhã' : 'Tarde'} (horário a definir)
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      slot.quantidade_disponivel > 3 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-amber-100 text-amber-800'
                                    }`}>
                                      {slot.quantidade_disponivel} vagas
                                    </span>
                                    {formData.slot_id === slot.id && (
                                      <Check className="h-4 w-4 text-teal-600" />
                                    )}
                                  </div>
                                </div>
                                <div className="text-sm text-slate-600 mt-1">
                                  Profissional: {slot.profissional}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {vagasListOpen && availableSlots.length === 0 && (
                        <div className="mt-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-amber-800">
                            Nenhuma vaga disponível para <strong>{formData.sigla}</strong>.
                          </p>
                          <p className="text-xs text-amber-600 mt-1">
                            Entre em contato com a administração para verificar o calendário.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Campo de Horário - quando slot não tem horário definido */}
                {formData.slot_id && slotSemHorario && (
                  <div className="md:col-span-6">
                    <Label htmlFor="manual_time">Horário do Atendimento *</Label>
                    {horariosPermitidos.length > 0 ? (
                      <>
                        <select
                          id="manual_time"
                          value={formData.appointment_time}
                          onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                          required
                          className="flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                          data-testid="manual-time-select"
                        >
                          <option value="">Selecione o horário</option>
                          {horariosPermitidos.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        <p className="text-xs text-teal-600 mt-1">
                          Horários definidos pelo calendário de regulação para {formData.sigla}.
                        </p>
                      </>
                    ) : (
                      <>
                        <Input
                          id="manual_time"
                          type="time"
                          value={formData.appointment_time}
                          onChange={(e) => {
                            const time = e.target.value;
                            const turno = formData._slot_turno;
                            if (turno === 'M' && time >= '12:00') {
                              toast.error('Turno da manhã: horário deve ser entre 07:00 e 12:00');
                              return;
                            }
                            if (turno === 'T' && time < '12:00') {
                              toast.error('Turno da tarde: horário deve ser entre 12:00 e 18:00');
                              return;
                            }
                            setFormData({ ...formData, appointment_time: time });
                          }}
                          min={formData._slot_turno === 'T' ? '12:00' : '07:00'}
                          max={formData._slot_turno === 'M' ? '12:00' : '18:00'}
                          required
                          className="h-11 rounded-lg"
                          data-testid="manual-time-input"
                        />
                        <p className="text-xs text-amber-600 mt-1">
                          Turno: {formData._slot_turno === 'M' ? 'Manhã (07:00 - 12:00)' : formData._slot_turno === 'T' ? 'Tarde (12:00 - 18:00)' : 'Não definido'}. Informe o horário dentro do turno da vaga.
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Checkbox de Jejum */}
                <div className="md:col-span-12">
                  <div 
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.requires_fasting 
                        ? 'bg-amber-50 border-amber-400' 
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setFormData({ ...formData, requires_fasting: !formData.requires_fasting })}
                    data-testid="fasting-checkbox"
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                      formData.requires_fasting 
                        ? 'bg-amber-500 border-amber-500' 
                        : 'bg-white border-slate-300'
                    }`}>
                      {formData.requires_fasting && <Check className="h-4 w-4 text-white" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <Utensils className={`h-5 w-5 ${formData.requires_fasting ? 'text-amber-600' : 'text-slate-400'}`} />
                      <div>
                        <span className={`font-medium ${formData.requires_fasting ? 'text-amber-800' : 'text-slate-700'}`}>
                          Paciente deve estar em JEJUM
                        </span>
                        <p className="text-xs text-slate-500">
                          Marque esta opção se o procedimento exige jejum. Esta informação aparecerá no cartão de confirmação.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Local de Atendimento */}
                <div className="md:col-span-12">
                  <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="h-5 w-5 text-blue-600" />
                      <Label className="text-sm font-semibold text-blue-800 !mb-0">Local de Atendimento</Label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="local_atendimento" className="text-xs text-slate-600">Nome do Local</Label>
                        <Input
                          id="local_atendimento"
                          value={formData.local_atendimento}
                          onChange={(e) => setFormData({ ...formData, local_atendimento: e.target.value })}
                          placeholder="Ex: Hospital Eurídice Santana"
                          className="h-10 rounded-lg"
                          data-testid="local-atendimento-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="local_endereco" className="text-xs text-slate-600">Endereço</Label>
                        <Input
                          id="local_endereco"
                          value={formData.local_endereco}
                          onChange={(e) => setFormData({ ...formData, local_endereco: e.target.value })}
                          placeholder="Endereço completo"
                          className="h-10 rounded-lg"
                          data-testid="local-endereco-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Campo Nº REG - Número de Registro */}
                <div className="md:col-span-6">
                  <Label htmlFor="registration_number">Nº REG. (Número de Registro)</Label>
                  <Input
                    id="registration_number"
                    value={formData.registration_number}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    placeholder="Ex: 12345"
                    className="h-11 rounded-lg"
                    data-testid="registration-number-input"
                  />
                </div>

                <div className="md:col-span-6">
                  <Label htmlFor="notes">Observações (aparecerá no cartão de confirmação)</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Observações adicionais que aparecerão no cartão de confirmação (opcional)"
                    className="w-full h-20 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                    data-testid="appointment-notes-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="save-appointment-button"
                >
                  Marcar Consulta
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isDeadlinePassed() && (
        <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-rose-600" />
          <p className="text-sm text-rose-700 font-medium">
            Prazo para marcações encerrado. Apenas visualização disponível.
          </p>
        </div>
      )}

      {/* Barra de Busca */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por nome do paciente ou cartão SUS..."
            className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white shadow-sm"
            data-testid="search-appointments"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-medium"
            >
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Filtros de Data */}
      <div className="mb-4 flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-teal-600" />
          <span className="text-sm font-medium text-slate-700">Mês:</span>
        </div>
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => { setFilterMonth(e.target.value); setFilterDataInicio(''); setFilterDataFim(''); }}
          className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
          data-testid="filter-month"
        />
        {filterMonth && (
          <button
            onClick={() => setFilterMonth('')}
            className="h-9 px-3 rounded-lg bg-slate-100 text-slate-600 text-sm hover:bg-slate-200 transition-colors"
            data-testid="clear-month-filter"
          >
            Todos os meses
          </button>
        )}
        
        <div className="h-8 w-px bg-slate-200 mx-1"></div>
        
        <span className="text-sm font-medium text-slate-700">Período:</span>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filterDataInicio}
            onChange={(e) => { setFilterDataInicio(e.target.value); if (e.target.value) setFilterMonth(''); }}
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
            data-testid="filter-data-inicio"
          />
          <span className="text-slate-400 text-sm">até</span>
          <input
            type="date"
            value={filterDataFim}
            onChange={(e) => { setFilterDataFim(e.target.value); if (e.target.value) setFilterMonth(''); }}
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
            data-testid="filter-data-fim"
          />
        </div>
        {(filterDataInicio || filterDataFim) && (
          <button
            onClick={() => { setFilterDataInicio(''); setFilterDataFim(''); const now = new Date(); setFilterMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`); }}
            className="h-9 px-3 rounded-lg bg-slate-100 text-slate-600 text-sm hover:bg-slate-200 transition-colors"
            data-testid="clear-date-filter"
          >
            Limpar datas
          </button>
        )}
      </div>

      {/* Abas Hospital / Lab Municipal */}
      <div className="mb-4">
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
          <button
            onClick={() => setOriginTab('hospital')}
            data-testid="origin-tab-hospital"
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
              originTab === 'hospital'
                ? 'bg-teal-700 text-white shadow-md'
                : 'text-slate-500 hover:bg-white hover:text-slate-700'
            }`}
          >
            <Building2 className="h-4 w-4" />
            Hospital
          </button>
          <button
            onClick={() => setOriginTab('lab_municipal')}
            data-testid="origin-tab-lab"
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all ${
              originTab === 'lab_municipal'
                ? 'bg-amber-600 text-white shadow-md'
                : 'text-slate-500 hover:bg-white hover:text-slate-700'
            }`}
          >
            <FlaskConical className="h-4 w-4" />
            Lab. Municipal
          </button>
        </div>
      </div>

      {/* Abas de Status */}
      <div className="mb-6">
        <div className="flex items-center gap-1 bg-white p-1.5 rounded-xl border border-slate-200 shadow-sm">
          {[
            { key: 'Marcada', label: 'Marcadas', color: 'bg-blue-600', badge: 'bg-blue-100 text-blue-700' },
            { key: 'Realizada', label: 'Realizadas', color: 'bg-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key.toLowerCase()}`}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.key
                  ? `${tab.color} text-white shadow-md`
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
              }`}
            >
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-white/20 text-white' : tab.badge
              }`}>
                {tabCounts[tab.key] || 0}
              </span>
            </button>
          ))}
          
          <div className="h-8 w-px bg-slate-200 mx-2"></div>
          
          {/* Filtro de tipo */}
          <select
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white"
            data-testid="filter-tipo"
          >
            <option value="">Todos os tipos</option>
            <option value="Consulta">Consultas</option>
            <option value="Exame">Exames</option>
          </select>
          
          {/* Filtro de unidade */}
          {hasFullAccess && units.length > 1 && (
            <select
              value={filterUnit}
              onChange={(e) => setFilterUnit(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-white max-w-[200px]"
              data-testid="filter-unit"
            >
              <option value="">Todas as unidades</option>
              {units.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
          
          <div className="ml-auto text-sm text-slate-500 px-3 whitespace-nowrap">
            Total: <span className="font-bold text-slate-700">{totalAppt}</span> | Exibindo: <span className="font-bold text-slate-700">{appointments.length}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Info de paginação */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <span className="text-sm text-slate-600" data-testid="appointments-count-info">
            {totalAppt} agendamento(s)
            {totalAppt > APPT_PER_PAGE && (
              <> — Página {apptPage} de {Math.ceil(totalAppt / APPT_PER_PAGE)}</>
            )}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider font-semibold text-xs h-12">
              <tr>
                <th className="px-6 py-3">Paciente</th>
                <th className="px-6 py-3">Procedimento</th>
                <th className="px-6 py-3">Médico</th>
                <th className="px-6 py-3">Unidade</th>
                <th className="px-6 py-3 cursor-pointer select-none hover:text-teal-700 transition-colors" onClick={() => setSortOrder(s => s === 'desc' ? 'asc' : 'desc')} data-testid="sort-date-toggle">
                  <span className="inline-flex items-center gap-1">
                    Data/Hora
                    {sortOrder === 'desc' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                  </span>
                </th>
                <th className="px-6 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {appointments.length > 0 ? (
                appointments
                  .map((appt) => (
                  <tr key={appt.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors h-14">
                    <td className="px-6 py-3 font-medium text-slate-900">
                      {appt.patient?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-3 text-slate-600">
                      <span className="font-medium">{appt.appointment_type}</span>
                      {/* Mostrar múltiplos procedimentos para LAB */}
                      {appt.procedure_names && appt.procedure_names.length > 0 ? (
                        <span className="text-xs text-slate-500 block truncate max-w-[200px]" title={appt.procedure_names.join(', ')}>
                          {appt.procedure_names.length} exame(s): {appt.procedure_names.slice(0, 2).join(', ')}
                          {appt.procedure_names.length > 2 && '...'}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500 block truncate max-w-[200px]" title={appt.procedure_name || appt.consultation_type || appt.exam_type}>
                          {appt.procedure_name || appt.consultation_type || appt.exam_type || '-'}
                        </span>
                      )}
                      {appt.procedure_codes && appt.procedure_codes.length > 0 ? (
                        <span className="text-xs text-teal-600">{appt.procedure_codes.length} código(s)</span>
                      ) : appt.procedure_code && (
                        <span className="text-xs text-teal-600">{appt.procedure_code}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-600">{appt.doctor || 'Não informado'}</td>
                    <td className="px-6 py-3 text-slate-600">
                      {appt.unit?.name || 'N/A'}
                      {appt.local_atendimento && (
                        <span className="text-xs text-blue-600 block truncate max-w-[180px]" title={`${appt.local_atendimento} - ${appt.local_endereco || ''}`}>
                          {appt.local_atendimento}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-600 font-mono text-xs">
                      {formatDateBR(appt.appointment_date)} {appt.appointment_time}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadCartao(appt.id)}
                          className="h-8 text-teal-600 hover:bg-teal-50"
                          data-testid={`print-cartao-${appt.id}`}
                          title="Baixar comprovante de agendamento"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        {isAdminGeral && appt.status === 'Marcada' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(appt)}
                            className="h-8 text-blue-600 hover:bg-blue-50"
                            data-testid={`edit-appointment-${appt.id}`}
                            title="Editar data/hora"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCancel(appt.id)}
                          disabled={isDeadlinePassed() || appt.status === 'Realizada'}
                          className="h-8 text-rose-600 hover:bg-rose-50"
                          data-testid={`cancel-appointment-${appt.id}`}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                    Nenhuma consulta marcada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Controles de Paginação */}
        {totalAppt > APPT_PER_PAGE && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between" data-testid="appointments-pagination">
            <span className="text-sm text-slate-500">
              Mostrando {((apptPage - 1) * APPT_PER_PAGE) + 1} a {Math.min(apptPage * APPT_PER_PAGE, totalAppt)} de {totalAppt}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setApptPage(p => Math.max(1, p - 1))}
                disabled={apptPage === 1}
                className="h-9"
                data-testid="appt-prev-page"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              {Array.from({ length: Math.ceil(totalAppt / APPT_PER_PAGE) }, (_, i) => i + 1)
                .filter(page => {
                  const totalPages = Math.ceil(totalAppt / APPT_PER_PAGE);
                  return page === 1 || page === totalPages || Math.abs(page - apptPage) <= 1;
                })
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} className="px-2 text-slate-400">...</span>
                  ) : (
                    <Button
                      key={item}
                      variant={apptPage === item ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setApptPage(item)}
                      className={`h-9 w-9 p-0 ${apptPage === item ? 'bg-teal-700 hover:bg-teal-800' : ''}`}
                      data-testid={`appt-page-${item}`}
                    >
                      {item}
                    </Button>
                  )
                )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setApptPage(p => Math.min(Math.ceil(totalAppt / APPT_PER_PAGE), p + 1))}
                disabled={apptPage >= Math.ceil(totalAppt / APPT_PER_PAGE)}
                className="h-9"
                data-testid="appt-next-page"
              >
                Próxima
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Cadastro Rápido de Paciente */}
      <Dialog open={newPatientDialogOpen} onOpenChange={setNewPatientDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <UserPlus className="h-6 w-6 text-teal-600" />
              Cadastro Rápido de Paciente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewPatientSubmit} className="space-y-6 mt-4">
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
              <p className="text-sm text-sky-700">
                Cadastre o paciente rapidamente. Após o cadastro, ele será automaticamente selecionado para a marcação.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="new_patient_name">Nome Completo *</Label>
                <Input
                  id="new_patient_name"
                  value={newPatientData.name}
                  onChange={(e) => setNewPatientData({ ...newPatientData, name: e.target.value.toUpperCase() })}
                  placeholder="NOME COMPLETO DO PACIENTE"
                  required
                  className="h-11 rounded-lg uppercase"
                  data-testid="new-patient-name"
                />
              </div>

              <div>
                <Label htmlFor="new_patient_sus">Cartão SUS *</Label>
                <Input
                  id="new_patient_sus"
                  value={newPatientData.sus_card}
                  onChange={(e) => setNewPatientData({ ...newPatientData, sus_card: formatSUSCard(e.target.value) })}
                  placeholder="000 0000 0000 0000"
                  required
                  className="h-11 rounded-lg"
                  data-testid="new-patient-sus"
                />
              </div>

              <div>
                <Label htmlFor="new_patient_cpf">CPF</Label>
                <Input
                  id="new_patient_cpf"
                  value={newPatientData.cpf}
                  onChange={(e) => setNewPatientData({ ...newPatientData, cpf: formatCPF(e.target.value) })}
                  placeholder="000.000.000-00"
                  className="h-11 rounded-lg"
                  data-testid="new-patient-cpf"
                />
              </div>

              <div>
                <Label htmlFor="new_patient_birth">Data de Nascimento *</Label>
                <Input
                  id="new_patient_birth"
                  type="date"
                  value={newPatientData.birth_date}
                  onChange={(e) => setNewPatientData({ ...newPatientData, birth_date: e.target.value })}
                  required
                  className="h-11 rounded-lg"
                  data-testid="new-patient-birth"
                />
              </div>

              <div>
                <Label htmlFor="new_patient_phone">Telefone *</Label>
                <Input
                  id="new_patient_phone"
                  value={newPatientData.phone}
                  onChange={(e) => setNewPatientData({ ...newPatientData, phone: formatPhone(e.target.value) })}
                  placeholder="(00) 00000-0000"
                  required
                  className="h-11 rounded-lg"
                  data-testid="new-patient-phone"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="new_patient_address">Endereço *</Label>
                <Input
                  id="new_patient_address"
                  value={newPatientData.address}
                  onChange={(e) => setNewPatientData({ ...newPatientData, address: e.target.value.toUpperCase() })}
                  placeholder="RUA, NÚMERO, BAIRRO, CIDADE"
                  required
                  className="h-11 rounded-lg uppercase"
                  data-testid="new-patient-address"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="new_patient_unit">Unidade de Saúde *</Label>
                <Select 
                  value={newPatientData.unit_id} 
                  onValueChange={(value) => setNewPatientData({ ...newPatientData, unit_id: value })}
                  disabled={!canChangeUnit}
                >
                  <SelectTrigger className="h-11 rounded-lg" data-testid="new-patient-unit">
                    <SelectValue placeholder="Selecione a unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map(unit => (
                      <SelectItem key={unit.id} value={unit.id}>{unit.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!canChangeUnit && (
                  <p className="text-xs text-slate-500 mt-1">
                    Paciente será cadastrado na sua unidade de saúde.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setNewPatientDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="save-new-patient-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Cadastrar Paciente
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação com Cartão */}
      <Dialog open={confirmacaoDialogOpen} onOpenChange={setConfirmacaoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-teal-700">
              Agendamento Realizado!
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-10 w-10 text-teal-600" />
            </div>
            
            <p className="text-slate-600 mb-6">
              A consulta foi agendada com sucesso!<br />
              Deseja imprimir o cartão de confirmação?
            </p>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => handleDownloadCartao(ultimoAgendamentoId)}
                className="bg-teal-700 hover:bg-teal-800 w-full"
                data-testid="download-cartao-button"
              >
                <FileDown className="h-5 w-5 mr-2" />
                Baixar Comprovante de Agendamento (PDF)
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => setConfirmacaoDialogOpen(false)}
              >
                Fechar
              </Button>
            </div>
            
            <p className="text-xs text-slate-500 mt-4">
              O cartão contém todas as informações necessárias para o atendimento.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição de Consulta */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Editar Agendamento
            </DialogTitle>
          </DialogHeader>
          {editingAppt && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-900">{editingAppt.patient_name}</p>
                <p className="text-slate-500">{editingAppt.procedure_name || editingAppt.sigla} - {editingAppt.sigla}</p>
                <p className="text-slate-500">
                  Vaga atual: {editingAppt.appointment_date ? new Date(editingAppt.appointment_date + 'T12:00:00').toLocaleDateString('pt-BR') : ''} {editingAppt.appointment_time}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Selecione uma nova vaga disponível ({editingAppt.sigla})</Label>
                {editSlots.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                    Nenhuma vaga disponível para {editingAppt.sigla}
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                    {editSlots.map(slot => (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => handleSelectEditSlot(slot)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between ${
                          editSlotId === slot.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        data-testid={`edit-slot-${slot.id}`}
                      >
                        <div>
                          <span className="font-medium text-slate-900">
                            {new Date(slot.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                          </span>
                          <span className="text-slate-500 ml-2">
                            {slot.horario || (slot.turno === 'M' ? 'Manhã' : slot.turno === 'T' ? 'Tarde' : '')}
                          </span>
                          <span className="text-slate-400 ml-2">- {slot.profissional}</span>
                        </div>
                        <span className="text-xs text-green-600 font-medium">
                          {slot.quantidade_disponivel}/{slot.quantidade_total}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {editSlotId && editSlotSemHorario && (
                <div>
                  <Label className="text-sm font-medium mb-1 block">Horário do Atendimento *</Label>
                  <Input
                    type="time"
                    value={editTime}
                    onChange={(e) => {
                      const time = e.target.value;
                      if (editSlotTurno === 'M' && time >= '12:00') {
                        toast.error('Turno da manhã: horário deve ser entre 07:00 e 12:00');
                        return;
                      }
                      if (editSlotTurno === 'T' && time < '12:00') {
                        toast.error('Turno da tarde: horário deve ser entre 12:00 e 18:00');
                        return;
                      }
                      setEditTime(time);
                    }}
                    min={editSlotTurno === 'T' ? '12:00' : '07:00'}
                    max={editSlotTurno === 'M' ? '12:00' : '18:00'}
                    className="h-11"
                    data-testid="edit-time-input"
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    Turno: {editSlotTurno === 'M' ? 'Manhã (07:00 - 12:00)' : editSlotTurno === 'T' ? 'Tarde (12:00 - 18:00)' : 'Não definido'}
                  </p>
                </div>
              )}

              {editSlotId && !editSlotSemHorario && (
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                  Nova data: <strong>{new Date(editDate + 'T12:00:00').toLocaleDateString('pt-BR')}</strong> às <strong>{editTime}</strong>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleEditAppointment}
                  disabled={editLoading || !editSlotId}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-edit-button"
                >
                  {editLoading ? 'Salvando...' : 'Salvar Alteração'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
